import React, { useState, useMemo } from 'react';
import {
  Button, CircularProgress, makeStyles, createTheme, CssBaseline,
  ThemeProvider, Hidden, DialogContentText,
} from '@material-ui/core';
import {
  Route, Routes, useLocation, useNavigate
} from 'react-router-dom';
import api, { setKey } from '../services/api';
import { TimeSelect } from './TimeSelect.page';
import { ContactPage } from './Contact.page';
import { ConfirmationPage } from './Confirmation.page';
import { useFormik } from 'formik';
import {
  validateRequired, validateEmail, validatePhone, validateBirthday
} from './validation';
import {
  format, subMinutes, formatRFC3339, addDays, isEqual,
  startOfDay,
} from 'date-fns';
import {
  utcToZonedTime, zonedTimeToUtc,
} from 'date-fns-tz';
import CloseIcon from '@material-ui/icons/Close';
import {
  Settings, TimeOption, ApiState, Contact, NewClientBookingContainer,
  Time, GetClient, GetClientSuccess, SomeClient, BusyBody, Busy,
} from './np-types';
import { AxiosResponse } from 'axios';
import Modal from './components/Modal/modal.component';
import LogoIcon from './Logo.icon';
import { splitEvery, drop, take } from 'ramda';

function useEffectDebounce(cb: () => void, values: any[], delay: number) {
  React.useEffect(() => {
    const handler = setTimeout(() => {
      cb();
    }, delay);

    return () => clearTimeout(handler);
  }, [...values, delay]);
}


/*
* Put this on the high level page
* with the iFrame code to link the two systems
<script>
  console.log('add event listener!');
  var listenFunction = (event)  => {
    console.log('child message!', event.data, event.origin);
    var data = JSON.parse(event.data);
    if(data.actionType === 'get-user') {
      console.log('valid data!');
      var contact = localStorage._ud;
      console.log('send to iFrame', contact);
      document
        .getElementById('sked-portal')
        .contentWindow
        .postMessage(contact, event.origin);
    } else if(data.actionType === 'confirm') {
      console.log('got confimration email', data.data);
      document.querySelector('#_builder-form input').value = data.data;
      setTimeout(() => {
        document.querySelector('#_builder-form button').click();
      }, 10e3);
    }

  };

  window.addEventListener("message", listenFunction, false);
</script>
*/

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthday: string;
}

const getHighlevelData = (): Promise<Contact> => {
  return new Promise((res, rej) => {

    const listenFunction = (event: MessageEvent) => {
      try {
        const customer = JSON.parse(event.data) as Customer;
        const contact = {
          firstName: customer.first_name,
          lastName: customer.last_name,
          email: customer.email,
          phone: customer.phone,
          referrer: '',
          otherReferrer: '',
          properPhoneLength: 10,
          birthday: customer.birthday,
        };
        res(contact);
      } catch (e) {
        console.log('error parsing json data');
        rej(e);
      } finally {
        window.removeEventListener('message', listenFunction);
      }
    };

    window.addEventListener('message', listenFunction, false);

    parent.postMessage(JSON.stringify({ actionType: 'get-user' }), '*');

    setTimeout(() => {
      console.log('did not get a response - abort listener');
      window.removeEventListener('message', listenFunction);
      rej(new Error('timeout'));
    }, 2e3);
  });
};

const postToParent = (
  name: string, email: string, phone: string, utc: string,
  time: string, timezone: string, td: string,
  redirectionUrl: string
) => {
  const d = `${utc} ${time} ${timezone}`;
  const formatDt = format(new Date(td), 'MM-dd-yyyy hh:mm a');

  const message = JSON.stringify({
    actionType: 'confirm',
    data: email,
    url: redirectionUrl,
    payload: {
      name,
      email,
      date: d,
      systemDate: formatDt,
      phone,
    }
  });
  console.log('send to parent frame', message);
  parent.postMessage(message, '*');
};

/*const updateFavicon = (ref: string) => {
  // Couldn't find the right type for this - link
  let link: any = document.querySelector('link[rel~=\'icon\']');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  link.href = ref;
};*/


const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1000px',
    width: 'fit-content',
    paddingLeft: '24px',
    paddingRight: '24px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '40px'
  },
  error: {
    color: theme.palette.error.main
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    width: 'fit-content',
    position: 'relative',
  },
  bottomNav: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& button': {
      textTransform: 'none',
    }
  },
  warningContainer: {
    backgroundColor: '#fff9e0',
    padding: '30px 15px 15px 15px',
    borderRadius: 15,
    position: 'relative',
    marginTop: 10,
  },
  warningTitle: {
    position: 'absolute',
    top: 0,
    padding: '1px 4px',
    backgroundColor: '#ffc713',
    borderRadius: 5,
    fontWeight: 500,
  },
  closeIcon: {
    position: 'absolute',
    top: 0,
    right: 0,
    margin: 5,
    cursor: 'pointer',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 13,
    position: 'absolute',
    top: 36,
    right: 0,
    bottom: 'unset',
    width: 163,
    marginLeft: 'unset',
    marginRight: 'unset',
    left: 'unset',
  },
  logoTime: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 13,
    position: 'absolute',
    top: 36,
    right: 0,
    bottom: 'unset',
    width: 163,
    marginLeft: 'unset',
    marginRight: 'unset',
    left: 'unset',
  },
  '@media screen and (max-width: 992px)': {
    logo: {
      bottom: 'unset',
      top: 36,
      right: 0,
      marginLeft: 'unset',
      marginRight: 'unset',
      left: 'unset',
    },
    logoTime: {
      top: 'unset',
      right: 0,
      left: 0,
      bottom: -20,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  '@media screen and (max-width: 872px)': {
    logo: {
      top: 'unset',
      right: -87,
      bottom: 27,
      left: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  '@media screen and (max-width: 700px)': {
    logo: {
      top: 'unset',
      right: 0,
      left: 0,
      bottom: -20,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  modal: {
    maxWidth: '630px !important',
    '& a': {
      color: '#008BCF',
    }
  },
  smallerModal: {
    maxWidth: '540px !important',
    '& a': {
      color: '#008BCF',
    }
  },
}));

let apiKey = '';
let fromKey = '';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#63994d',
    }
  },
  typography: {
    fontFamily: [
      'Poppins',
      'Roboto',
      'Arial',
    ].join(','),
  },
});


const checkIfExisting = (values: Contact): Promise<
  AxiosResponse<GetClientSuccess>
> => {
  return api.post<
          GetClient, AxiosResponse<GetClientSuccess>
        >('client', {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone?.length > 4 ? values.phone.replace(/[{()}]|-|\s/g, '') : undefined,
          email: values.email?.trim() === '' ? undefined : values.email,
        });
};

const Logo = ({ className }: { className: string }) => {
  return (
    <div className={className}>
      Powered By <LogoIcon />
    </div>
  );
};

const defaultContact = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  referrer: '',
  birthday: ''
};

export const stillAvailable = (
  time: TimeOption, times: string[], timezone: string, scheduleCutoff: number,
  isTest?: string,
) => {
  const currentOfficeTime = isTest || formatRFC3339(
    utcToZonedTime(
      new Date(),
      timezone
    ));
  const outsideScheduleCutoff = (zonedTime: any) => {
    if (!scheduleCutoff) {
      return true;
    }
    const earliestTime = formatRFC3339(
      subMinutes(
        zonedTime,
        scheduleCutoff
      ));
    return currentOfficeTime < earliestTime;
  };
  return Boolean(times.filter((d) => {
    const zonedTime = utcToZonedTime(new Date(d), timezone);
    return outsideScheduleCutoff(zonedTime) &&
                currentOfficeTime < d;
  }).find((d) => {
    return d === time?.date;
  }));
};

const prettifyPhone = (phone: string) => {
  const country = take(2, phone);
  if (country !== '+1') {
    return phone;
  }
  const withoutCountry = drop(2, phone);
  const [area, firstBit, secondBit, rest] = splitEvery(3, withoutCountry);
  return `(${area}) ${firstBit}-${secondBit}${rest}`;
};

const NewPatient = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  //const {path} = useRouteMatch();
  const { pathname } = location;
  const path = pathname.split('/')[0];

  const [date, changeDate] = useState(startOfDay(new Date()));
  const [time, setTime] = useState<TimeOption>(null);
  const [theme, setTheme] = useState(defaultTheme);
  const formRef = React.useRef<HTMLFormElement>(null);

  const [apiState, setApiState] = useState<ApiState<Settings>>({
    isLoading: false,
    errorMessage: '',
    data: {
      id: 0,
      newPatientTypes: [],
      phone: '',
      ehrSystem: '',
      timezone: '',
      bookingRedirectUrl: '',
      referrals: [],
      logoUrl: '',
      name: '',
      calendarDirections: '',
      contactDirection: '',
      landingPageDescription: '',
      confirmationDescription: '',
      primaryColor: '',
      secondaryColor: '',
      backgroundColor: '',
      allowSameDayScheduling: false,
      hidePro: false,
      birthday: '',
      birthdayOption: 'Required',
      nppType: 'New',
    }
  });
  const [apptType, setApptType] = useState<string>(null);
  const [client, setClient] = useState<SomeClient>(null);
  const [pickAgain, setPickAgain] = useState<boolean>(false);
  const [modalText, setModalText] = useState<{
    title: string, body: string, actions?: React.ReactNode[], className?: string
  }>(null);
  const [wasRedirected, setWasRedirected] = useState(false);
  const [scheduledOnLinkedPortal, setScheduledOnLinkedPortal] = useState(false);
  const [autoFocusIndex, setAutoFocusIndex] = useState(-1);

  const selectedApptType = useMemo(() => {
    if (client) {
      return {
        ...client.appointmentType,
        displayName: client.appointmentType.name,
        displayFirstName: 'Dr. SKED',
      };
    }
    return apiState.data.newPatientTypes.find((appt) => {
      return appt.appointmentTypeId === Number(apptType);
    });
  }, [apptType]);


  const [bookingState, setBookingState] = useState<ApiState<string>>({
    isLoading: false,
    errorMessage: '',
    data: ''
  });

  const handleTimeSelect = (t: TimeOption) => {
    setTime(t);
    console.log(t, client);
    if (client) {
      submitFromClient(t);
    } else {
      handleNext();
      const fields = [
        contactForm.values.firstName, contactForm.values.lastName,
        contactForm.values.email, contactForm.values.phone,
        apiState.data.birthdayOption === 'Hidden' ?
          'ignoreme' : contactForm.values.birthday,
      ];
      const index = fields.findIndex((v) => v === undefined || v.length === 0);
      if (wasRedirected && apiState.data.nppType === 'New' && index !== -1) {
        console.log('index: ', index, apiState.data.birthdayOption);
        setAutoFocusIndex(index);
      } else {
        setAutoFocusIndex(-1);
      }
      if (wasRedirected &&
          (apiState.data.birthdayOption === 'Hidden' ||
           contactForm.values.birthday?.length > 0 ||
           (apiState.data.birthdayOption === 'Required' || index < 4))) {
        contactForm.handleSubmit();
      }
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const key = searchParams.get('key');
    const from = searchParams.get('from');
    const auth = searchParams.get('auth');
    if (key) {
      apiKey = key;
      setKey(key);
    }
    if (from) {
      fromKey = from;
    }

    setApiState(s => ({ ...s, errorMessage: '', isLoading: true }));

    api.get<Settings>('office')
      .then(({ data }) => {
        //updateFavicon('/assets/right-logo-new.png');
        window.document.title = data.name;
        console.log(from, data);
        const now = startOfDay(new Date());
        if (!data.allowSameDayScheduling && isEqual(date, now)) {
          changeDate(addDays(date, 1));
        }
        setApiState(s => ({
          ...s,
          data: {
            ...data,
            linkedKey: from || data.linkedKey,
          },
        }));

        const officeTheme = createTheme({
          palette: {
            primary: {
              main: data.primaryColor || '#008BCF'
            },
            secondary: {
              main: data.secondaryColor || '#008BCF'
            },
            background: {
              default: data.backgroundColor || '#f7f9fc'
            }
          },
          typography: {
            fontFamily: [
              'Poppins',
              'Roboto',
              'Arial',
            ].join(','),
          },
        });
        setTheme(officeTheme);

        if (data.newPatientTypes.length === 1) {
          const firstType = data.newPatientTypes[0];
          setApptType(String(firstType.appointmentTypeId));
        } else if (client && client.fallbackEnforced) {
          setApptType(String(client.appointmentType.id));
        }

        if (data.nppType === 'New' || client) {
          gotoTimeSelector();
          setApiState(s => ({
            ...s,
            isLoading: false,
          }));
        } else {
          if (auth) {
            api.post<GetClientSuccess>('authtoken', { token: auth })
              .then((cData) => {
                const {
                  success, client
                } = cData.data;
                if (success) {
                  setClient(client);
                  if (data.newPatientTypes.length === 0 || client.fallbackEnforced) {
                    setApptType(String(client.appointmentType.id));
                  }
                  gotoTimeSelector();
                } else {
                  handleNext();
                }
                setApiState(s => ({
                  ...s,
                  isLoading: false,
                }));
              }).catch(() => {
                setApiState(s => ({
                  ...s,
                  isLoading: false,
                }));
                handleNext();
              });
          } else {
            if (!auth || !apptType)
              handleNext();
            setApiState(s => ({
              ...s,
              isLoading: false,
            }));
          }
        }
      })
      .catch(e => setApiState(s => ({ ...s, isLoading: false, errorMessage: e.message })));

    if (location.pathname !== path) {
      gotoTimeSelector();
    }
  }, [location.search]);

  const handleDateChange = (d: Date) => {
    //     if (!wasRedirected || forceClear) {
    setTime(null);
    //     }
    changeDate(d);
  };

  const handleNext = () => {
    navigate(`/new-patient/contact?key=${apiKey}&from=${fromKey}`);
  };

  const gotoTimeSelector = () => {
    navigate(`/new-patient/time?key=${apiKey}&from=${fromKey}`);
  };

  const gotoConfirm = () => {
    /* don't go to the confirm page if the redirect url is enabled */
    if (apiState.data.redirectionUrl) {
      /* when you're not in an iframe, do the redirect here. */
      if (window.self === window.top) {
        window.location.href = apiState.data.redirectionUrl;
      }
    } else {
      navigate(`/new-patient/confirmation?key=${apiKey}&from=${fromKey}`);
    }
  };

  /* When we are going to a new patient, we want to clear the client
     and apptType because of the api.get('office') request above.
     We want to get the new appt types and use those, not the existing
     client's
  */
  const gotoOther = (linkedUrl: string) => {
    if (apiState.data.nppType === 'Existing') {
      setClient(null);
      setApptType(null);
    }
    navigate(linkedUrl);
    setWasRedirected(true);
    setPickAgain(false);
  };

  const restart = () => {
    if (!client) {
      contactForm.resetForm();
    }
    if (isNew) {
      contactForm.resetForm();
      setWasRedirected(false);
      setClient(null);
      setPickAgain(false);
      setTime(null);
      gotoTimeSelector();
      return;
    }
    if (scheduledOnLinkedPortal) {
      setScheduledOnLinkedPortal(false);
      gotoOther(`/new-patient/time?key=${apiState.data.linkedKey}&from=${apiKey}`);
    } else {
      checkIfExisting(contactForm.values).then(({ data }) => {
        if (data.success) {
          setClient(data.client);
          gotoTimeSelector();
        }
      });
    }
    setWasRedirected(false);
  };

  const isNew = useMemo(() => {
    return apiState.data?.nppType === 'New';
  }, [apiState]);

  /* If we only have the client, meaning that they've verified using the
     auth query param, we won't have the form data and so we can't use
     the normal submit function. This only uses the client state.
  */
  const submitFromClient = (time: TimeOption) => {
    setApiState(s => ({ ...s, errorMessage: '', isLoading: true }));
    if (!time?.date) {
      setApiState(s => ({
        ...s,
        errorMessage: 'Missing date, please select time again.',
        isLoading: false
      }));
      return;
    }
    const timeInUTC = zonedTimeToUtc(
      time.date,
      apiState.data.timezone
    );
    const body: NewClientBookingContainer = {
      newClient: {
        firstName: client?.firstName,
        lastName: client?.lastName,
        appointmentType: Number(apptType),
        time: formatRFC3339(timeInUTC),
        appointmentTypeName: selectedApptType?.displayName,
        providerName: selectedApptType.displayFirstName,
        referrer: 'Other',
        phone: '+14444444444',
        birthday: undefined,
        isLead: false,
        email: 'existing@example.com'
      },
      token: client?.token,
    };
    setBookingState(s => ({ ...s, errorMessage: '', isLoading: true }));

    const timesBody: BusyBody = {
      appointmentType: Number(apptType),
      date: time.localDate,
      clients: 1,
    };

    return api.post<BusyBody, AxiosResponse<Busy[]>>('busyness', timesBody)
      .then((busynessData) => {
        const data = busynessData.data.map(({ time }) => time);
        const settings = apiState.data;
        const isAvailable = stillAvailable(
          time, data, settings.timezone, selectedApptType?.scheduleCutoff
        );
        if (isAvailable) {
          return api.post<
              NewClientBookingContainer, AxiosResponse<null>
            >('sked', body)
            .then(() => {
              try {
                //Doesn't work in iFrame may have to use a cookie...
                localStorage.skedNu = '{}';
              } catch (e) {
                console.log('cant access localStorage');
              } finally {
                if(!apiState.data.redirectionUrl) {
                  setBookingState(s => ({ ...s, isLoading: false }));
                  setApiState(s => ({ ...s, errorMessage: '', isLoading: false }));
                }
                setPickAgain(false);
                gotoConfirm();
                const name = client?.firstName + ' ' + client?.lastName;
                const strippedPhone = '+14444444444'.replace(
                  /[{()}]|-|\s/g, ''
                );
                postToParent(
                  name, 'example@ex.com', strippedPhone,
                  format(date, 'EEE, MMM d, yyyy'), time.display,
                  apiState.data.timezone, time.date, apiState.data.redirectionUrl,
                );
              }
            });
        } else {
          setApiState(s => ({ ...s, errorMessage: '', isLoading: false }));
          //           setModalText({
          //             title: 'Scheduling Error',
          //             body: `The appointment time that you selected is no longer available.
          // Please select another time.`
          //           });
          setPickAgain(true);
          gotoTimeSelector();
          return;
        }
      })
      .catch(e => {
        setBookingState(s => ({
          ...s,
          isLoading: false,
          errorMessage: e.response?.data || e.message,
        }));
      });
  };

  const contactForm = useFormik<Contact>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      referrer: '',
      otherReferrer: '',
      properPhoneLength: 10,
      birthday: '',
    },
    validate: (values) => {
      const r = validateRequired<Contact>(values);
      const email = !isNew && values.phone
        ? {} : validateEmail<Contact>(values)('email', 'Please enter a valid email address');
      const phone = !isNew && values.phone
        ? {} : validatePhone<Contact>(values)('phone', 'properPhoneLength', 'Please enter a valid phone number');
      const birthday = validateBirthday<Contact>(values);

      const birthdayIsValid = isNew &&
        apiState.data.birthdayOption !== 'Hidden' ?
        birthday('birthday', 'Please enter a valid birthday') : {};
      const birthdayIsRequired = isNew &&
        apiState.data.birthdayOption === 'Required' ?
        r('birthday', 'A birthday is required') : {};

      let emailIsRequired = r('email', 'Please enter your email address');
      let phoneIsRequired = r('phone', 'Please enter your phone number');
      if (!isNew) {
        emailIsRequired = !values.phone?.trim() && emailIsRequired;
        phoneIsRequired = !values.email?.trim() && phoneIsRequired;
      }

      return {
        ...email,
        ...phone,
        ...emailIsRequired,
        ...r('firstName', 'Please enter your first name'),
        ...r('lastName', 'Please enter your last name'),
        ...phoneIsRequired,
        ...birthdayIsValid,
        ...birthdayIsRequired,
      };
    },
    onSubmit: (values, { setSubmitting }) => {
      if (isNew || client) {
        console.log(selectedApptType);
        const timeInUTC = zonedTimeToUtc(
          time.date,
          apiState.data.timezone
        );
        const body: NewClientBookingContainer = {
          newClient: {
            ...values,
            appointmentType: Number(apptType),
            time: formatRFC3339(timeInUTC),
            appointmentTypeName: selectedApptType?.displayName,
            providerName: selectedApptType.displayFirstName,
            referrer: values.referrer === 'Other' ? values.otherReferrer : values.referrer,
            phone: client ? '+14444444444' : values.phone.replace(/[{()}]|-|\s/g, ''),
            birthday: !client && apiState.data.birthdayOption !== 'Hidden' ?
              values.birthday : undefined,
            isLead: true,
            email: client ? 'existing@example.com' : values.email,
          },
          token: client?.token,
        };
        setBookingState(s => ({ ...s, errorMessage: '', isLoading: true }));

        const timesBody: BusyBody = {
          appointmentType: Number(apptType),
          date: time.localDate,
          clients: 1,
        };

        return api.post<BusyBody, AxiosResponse<Busy[]>>('busyness', timesBody)
          .then((busynessData) => {
            const data = busynessData.data.map(({ time }) => time);
            const settings = apiState.data;
            const isAvailable = stillAvailable(
              time, data, settings.timezone, selectedApptType?.scheduleCutoff
            );
            if (isAvailable) {
              return api.post<
              NewClientBookingContainer, AxiosResponse<null>
            >('sked', body)
                .then(() => {
                  try {
                  //Doesn't work in iFrame may have to use a cookie...
                    localStorage.skedNu = '{}';
                  } catch (e) {
                    console.log('cant access localStorage');
                  } finally {
                    if(!apiState.data.redirectionUrl) {
                      setBookingState(s => ({ ...s, isLoading: false }));
                      setSubmitting(false);
                    }
                    setPickAgain(false);
                    gotoConfirm();
                    const name = values.firstName + ' ' + values.lastName;
                    const strippedPhone = values.phone.replace(
                      /[{()}]|-|\s/g, ''
                    );
                    postToParent(
                      name, values.email, strippedPhone,
                      format(date, 'EEE, MMM d, yyyy'), time.display,
                      apiState.data.timezone, time.date, apiState.data.redirectionUrl,
                    );
                  }
                });
            } else {
              //               setModalText({
              //                 title: 'Scheduling Error',
              //                 body: `The appointment time that you selected is no longer available.
              // Please select another time.`
              //               });
              setPickAgain(true);
              gotoTimeSelector();
              return;
            }
          })
          .catch(e => {
            setSubmitting(false);
            setBookingState(s => ({
              ...s,
              isLoading: false,
              errorMessage: e.response?.data || e.message,
            }));
          });
      } else {
        return checkIfExisting(values).then(({ data }) => {
          console.log('checking....', data);
          const isLead = data.errorMessage.includes('lead');
          if (data.success || (apiState.data.nppType === 'New' && isLead)) {
            setClient(data.client);
            if (apiState.data.newPatientTypes.length === 0 || data.client.fallbackEnforced) {
              setApptType(String(data.client.appointmentType.id));
            }
            gotoTimeSelector();
          } else if (apiState.data.linkedKey) {
            if (isLead) {
              navigate(`/new-patient/time?key=${apiState.data.linkedKey}&from=${apiKey}`);
              setModalText(null);
              return;
            }
            const inactive = data.errorMessage.includes('inactive');
            const prettyPhone = prettifyPhone(apiState.data.phone);
            const title = inactive ? 'Contact Our Office' : 'Is this your first time with us?';
            const body = inactive ?
              data.errorMessage.replace('${PHONE}', `<a href="tel:${apiState.data.phone}">${prettyPhone}</a>`) :
              'Looks like the information that you entered does not match our records. Try correcting your information provided or continue as a new patient.';
            setModalText({
              title,
              body,
              className: !inactive && classes.smallerModal,
              actions: !inactive && [
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  style={{ textTransform: 'unset' }}
                  disableElevation
                  onClick={() => {
                    navigate(`/new-patient/time?key=${apiState.data.linkedKey}&from=${apiKey}`);
                    setModalText(null);
                  }}
                >
                  I am a new patient
                </Button>
              ],
            });
            setSubmitting(false);
          } else {
            const prettyPhone = prettifyPhone(apiState.data.phone);
            setModalText({
              title: 'Contact Our Office',
              body: data.errorMessage.replace('${PHONE}', `<a href="tel:${apiState.data.phone}">${prettyPhone}</a>`),
            });
            setSubmitting(false);
          }
        });
      }
    }
  });

  const tryAgain = () => {
    gotoOther(`/new-patient/time?key=${apiState.data.linkedKey}&from=${apiKey}`);
    setPickAgain(true);
  };

  const handleSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (apiState.data.nppType === 'New') {
      contactForm.setSubmitting(true);
      let exist = { data: { success: false, client: null } as GetClientSuccess };
      try {
        exist = await checkIfExisting(contactForm.values);
      } catch (e) {
        console.log(e);
      }
      if (exist.data.success) {
        if (apiState.data.linkedKey) {
          setClient(exist.data.client);
          setKey(apiState.data.linkedKey);
          const newSettings = await api.get<Settings>('office');
          if (newSettings.data.newPatientTypes.length > 1) {
            return tryAgain();
          }
          if (exist.data.client.fallbackEnforced) {
            setApptType(String(exist.data.client.appointmentType.id));
          } else if (newSettings.data.newPatientTypes.length === 1) {
            setApptType(String(newSettings.data.newPatientTypes[0]));
          }
          const timesBody: Time = {
            appointmentTypeId: Number(apptType),
            start: time.localDate,
            end: time.localDate,
          };
          api.post<Time, AxiosResponse<string[]>>('times', timesBody)
            .then((tData) => {
              const times = tData.data;
              const hasTime = times.find((d: string) => {
                return d === time.date;
              });
              contactForm.setSubmitting(false);
              if (hasTime) {
                contactForm.handleSubmit();
                setScheduledOnLinkedPortal(true);
              } else {
                tryAgain();
              }
            }).catch(() => {
              tryAgain();
            });
        } else {
          contactForm.setSubmitting(false);
          setModalText({
            title: 'Contact Our Office',
            body: 'We apologize for the inconvenience. This scheduler isn\'t built for returning patients. We kindly ask you to contact our office.',
          });
        }
      } else if (exist.data.errorMessage?.includes('returning')) {
        contactForm.setSubmitting(false);
        const prettyPhone = prettifyPhone(apiState.data.phone);
        const msg = apiState.data.linkedKey ? `It looks like your account is currently inactive, so online scheduling isn't available. Please contact our office at <a href="tel:${apiState.data.phone}">${prettyPhone}</a>, and we'll be happy to assist you with booking your appointment.` : `We apologize for the inconvenience. This scheduler isn't set up for returning patients to schedule. Please contact our office at <a href="tel:${apiState.data.phone}">${prettyPhone}</a>, and we'll be happy to assist you with booking your appointment.`;
        setModalText({
          title: 'Contact Our Office',
          body: msg,
        });
      } else {
        contactForm.handleSubmit();
      }
    } else {
      contactForm.handleSubmit();
    }
  };

  React.useEffect(() => {
    getHighlevelData().then(data => {
      contactForm.setValues({
        ...defaultContact,
        ...data
      });
    }).catch(() => {
      try {
        const cachedContact = JSON.parse(localStorage.skedNu || '{}') as Contact;
        contactForm.setValues({
          ...defaultContact,
          ...cachedContact
        });
      } catch (e) {
        console.log('cannot access localStorage', e);
      }
    });
  }, []);

  useEffectDebounce(() => {
    const encoded = JSON.stringify(contactForm.values);
    try {
      localStorage.skedNu = encoded;
    } catch (e) {
      console.log('cannot save to localStorage', e);
    }
  }, [contactForm.values], 1e3);

  const [warningTitle, warningBody] = useMemo(() => {
    if (wasRedirected) {
      const name = apiState.data?.name || 'this office';
      return [
        'Oops! Looks like you\'re already an existing patient.',
        `You already have a patient account with ${name}. Please select a new date and time using this Existing Patient Scheduler.`
      ];
    } else {
      return [
        'Need to select another time',
        'We apologize for the inconvenience. It looks like the time slot you selected is no longer available. We kindly ask you to pick a different time.'
      ];
    }
  }, [pickAgain, wasRedirected]);

  if (apiState.isLoading) {
    return (
      <div style={{
        margin: '50px 0',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <CircularProgress />
      </div>
    );
  }

  if (!apiState.isLoading && apiState.errorMessage) {
    return (
      <div style={{
        margin: '50px 0',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <p>There was an error loading the new patient portal.  Please refresh and try again.</p>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className={classes.root}>
        {pickAgain &&
          <div className={classes.warningContainer}>
            <div className={classes.warningTitle}>
              {warningTitle}
            </div>
            <CloseIcon
              className={classes.closeIcon}
              onClick={() => setPickAgain(false)}
            />
            {warningBody}
          </div>}

        <Routes>
          <Route path="time" element={
            <React.Fragment>
              <div className={classes.formContainer}>
                <Logo className={classes.logoTime}/>
                <TimeSelect
                  date={date}
                  time={time}
                  apptType={apptType}
                  setApptType={setApptType}
                  selectedApptType={selectedApptType}
                  apiState={apiState}
                  handleDateChange={handleDateChange}
                  handleTimeSelect={handleTimeSelect}
                  apiKey={apiKey}
                  gotoOther={gotoOther}
                  setModalText={setModalText}
                  hide={client?.fallbackEnforced}
                />
                <div className={classes.bottomNav}>
                  <Hidden xsDown>
                    {!isNew &&
                    <Button
                      type="button"
                      onClick={() => {
                        setClient(null);
                        handleNext();
                      }}
                      variant="outlined"
                      color="primary"
                    >
                      Back
                    </Button>}
                  </Hidden>
                </div>
                <div style={{ flexGrow: 1 }}></div>
              </div>
            </React.Fragment>
          } />

          <Route path="contact" element={
            <form
              ref={formRef}
              className={classes.formContainer}
              onSubmit={handleSubmit}
              noValidate
            >
              <Logo className={classes.logo}/>
              <ContactPage
                back={gotoTimeSelector}
                date={date}
                time={time}
                settings={apiState.data}
                selectedApptType={selectedApptType}
                form={contactForm}
                apiKey={apiKey}
                gotoOther={gotoOther}
                autoFocus={autoFocusIndex}
                setModalText={setModalText}
              />
              {bookingState.errorMessage &&
                <div className={classes.bottomNav}>
                  <div style={{ flexGrow: 1 }}></div>
                  <div className={classes.error}>
                    {bookingState.errorMessage}
                  </div>
                </div>}
              <div className={classes.bottomNav}>
                <Hidden xsDown>
                  {isNew &&
                    <Button
                      type="button"
                      onClick={gotoTimeSelector}
                      variant="outlined"
                      color="primary"
                    >
                      Back
                    </Button>}
                  <div style={{ flexGrow: 1 }}></div>
                  {contactForm.isSubmitting &&
                    <CircularProgress size={20} />}
                  <Button
                    type={ 'submit' }
                    disabled={contactForm.isSubmitting}
                    variant="contained"
                    color="primary"
                    disableElevation
                  >
                    { isNew ? 'Submit' : 'Next' }
                  </Button>
                </Hidden>
                <Hidden smUp>
                  <div style={{ flexGrow: 1 }}></div>
                  <Button
                    type="submit"
                    size="large"
                    disabled={contactForm.isSubmitting}
                    variant="contained"
                    color="primary"
                    disableElevation>
                    { isNew ? 'Submit' : 'Next' }
                  </Button>
                  <div style={{ flexGrow: 1 }}></div>
                </Hidden>
              </div>
            </form>
          } />

          <Route path="confirmation" element={
            <div className={classes.formContainer}>
              <ConfirmationPage
                date={date}
                time={time}
                settings={apiState.data}
                selectedApptType={selectedApptType}
                contact={client ? {
                  ...defaultContact,
                  ...client,
                  otherReferrer: '',
                  properPhoneLength: 10,
                  birthday: '',
                } : contactForm.values} />
              <div className={classes.bottomNav}>
                <div style={{ flexGrow: 1 }}></div>
                <Button
                  type="button"
                  onClick={restart}
                  variant="outlined"
                  color="primary">
                  Start over
                </Button>
                <div style={{ flexGrow: 1 }}></div>
              </div>
            </div>
          } />
        </Routes>
      </div>
      <Modal
        title={modalText?.title}
        size='xs'
        open={Boolean(modalText)}
        onClose={() => setModalText(null)}
        buttons={modalText?.actions}
        className={modalText?.className || classes.modal}
      >
        <div>
          <DialogContentText>
            <div dangerouslySetInnerHTML={{ __html: modalText?.body }}>
            </div>
          </DialogContentText>
        </div>
      </Modal>
    </ThemeProvider>
  );
};

export default NewPatient;
